import React, { useEffect, useState } from 'react'
import GenerateIcon, { IconTypes } from '../../components/GenerateIcon'
import { useDispatch } from 'react-redux'
import ItemsApp from '../items/ItemsApp'
import { handleRequestError } from '../../services/api'
import { Skeleton } from '@mui/material'

interface IStatistics {
    total_items: number,
    total_warehouses: number,
    total_missing_items: number,
    total_damaged_items: number
}

export interface ISingleStatistic {
    title: string,
    number: number,
    icon: IconTypes,
    type: string,
    loading?: boolean
}

interface IAnimatedNumberProps {
    targetNumber: number,
    duration: number
}

const AnimatedNumber: React.FC<IAnimatedNumberProps> = (props) => {

    const { targetNumber, duration } = props

    const [currentNumber, setCurrentNumber] = useState(0);
  
    useEffect(() => {
      // Calculate the increment step and interval
      const steps = 100; // Number of animation steps
      const increment = targetNumber / steps;
      const interval = duration / steps;
  
      let currentStep = 0;
  
      const intervalId = setInterval(() => {
        currentStep += 1;
        setCurrentNumber((prev) => Math.min(prev + increment, targetNumber));
  
        if (currentStep >= steps) {
          clearInterval(intervalId);
        }
      }, interval);
  
      // Clean up the interval
      return () => clearInterval(intervalId);
    }, [targetNumber, duration]);
  
    return <>{Math.round(currentNumber)}</>
  }

export const SingleStatistic: React.FC<ISingleStatistic> = (props) => {

    const { title, number, icon, type, loading } = props

    return(
        <div className={"single-statistic-box " + type}>
            <div className="content-of-single-statistic-box">

                <div className="text-of-single-statistic-box">
                    <div className="content-of-text-of-single-statistic-box">
                        <h3>{loading ? <Skeleton variant="text" width={20} height={30} /> : <AnimatedNumber targetNumber={number} duration={100} />}</h3>
                        <h4>{loading ? <Skeleton variant="text" width={100} /> : title}</h4>
                    </div>
                </div>
                
                <div className="icon-of-single-statistic-box">
                    <div className="content-of-icon-of-single-statistic-box">
                        {loading ? <Skeleton variant="circular" width={20} height={20} /> : <GenerateIcon icon={icon} />}
                    </div>
                </div>

            </div>
        </div>
    )
}

export default function Statistics() {

    const dispatch = useDispatch()

    const [loading, setLoading] = useState<boolean>(true)
    const [statistics, setStatistics] = useState<IStatistics>({
        total_items: 0,
        total_warehouses: 0,
        total_missing_items: 0,
        total_damaged_items: 0
    })

    const [items, setItems] = useState<ISingleStatistic[]>([])

    const itemsApp = new ItemsApp()

    /**
     * Load statistics
     */
    const loadStatistics = () => {
        setLoading(true)

        itemsApp.getStatistics()
            .then(response => {
                setLoading(false)
                setStatistics(response.data)
            })
            .catch(error => {
                handleRequestError(error, dispatch)
            })

    }

    useEffect(() => {
        loadStatistics()
    }, [])

    useEffect(() => {
        setItems([
            {
                title: "Total warehouses",
                number: statistics.total_warehouses,
                icon: "inventory",
                type: "total_warehouses"
            },
            {
                title: "Total items",
                number: statistics.total_items,
                icon: "list",
                type: "total_items"
            },
            {
                title: "Missing items",
                number: statistics.total_missing_items,
                icon: "missing",
                type: "error"
            },
            {
                title: "Damaged items",
                number: statistics.total_damaged_items,
                icon: "repair",
                type: "error"
            }
        ])
    }, [statistics])

    const renderLoadingItems = () => {
        return(
            <>
                <SingleStatistic loading icon="home" title="" number={0} type="" />
                <SingleStatistic loading icon="home" title="" number={0} type="" />
                <SingleStatistic loading icon="home" title="" number={0} type="" />
                <SingleStatistic loading icon="home" title="" number={0} type="" />
            </>
        )
    }

    return(
        <div className="statistics-container">
            <div className="content-of-statistics-container">
                {loading ? renderLoadingItems() : items.map((item, index) => <SingleStatistic {...item} key={index} />)}
            </div>
        </div>
    )
}